import React, { useState, useEffect } from 'react';
import { IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent, IonIcon } from '@ionic/react';
import { close, restaurant, golfOutline, cartOutline, bagRemoveOutline, basketOutline, lockClosedOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';

export interface FilterOptions {
  country?: string;
  state?: string;
  minHoles?: number;
  maxHoles?: number;
  amenities?: string[];
  sortBy?: string;
  query?: string;
}


interface GolfClubFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterOptions) => void;
  initialFilters: FilterOptions;
  countries: { name: string; count: number }[];
  states: { name: string; count: number }[];
}

const GolfClubFilterModal: React.FC<GolfClubFilterModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  initialFilters,
  countries,
  states
}) => {
  const { t } = useTranslation('MGP');
  const [filterDraft, setFilterDraft] = useState<FilterOptions>(initialFilters);

  // Reset draft filters when modal opens with initial filters
  useEffect(() => {
    if (isOpen) {
      setFilterDraft({ ...initialFilters });
    }
  }, [isOpen, initialFilters]);

  // Apply filters and close modal
  const applyFilters = () => {
    onApplyFilters(filterDraft);
    onClose();
  };

  // Toggle an amenity in the filter
  const toggleAmenity = (amenity: string) => {
    const currentAmenities = filterDraft.amenities || [];
    const newAmenities = currentAmenities.includes(amenity)
      ? currentAmenities.filter(a => a !== amenity)
      : [...currentAmenities, amenity];

    setFilterDraft({
      ...filterDraft,
      amenities: newAmenities
    });
  };

  const isAmenitySelected = (amenity: string) => {
    return (filterDraft.amenities || []).includes(amenity);
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className="filter-modal">
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle>{t('filters')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {/* Country filter */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">{t('country')}</h3>
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="button"
              className={`py-2 px-4 rounded-full border border-solid ${!filterDraft.country ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => setFilterDraft({ ...filterDraft, country: undefined, state: undefined })}
            >
              <div className="text-center font-medium">{t('all')}</div>
            </button>
            {countries.map((country) => (
              <button
                key={country.name}
                type="button"
                className={`py-2 px-4 rounded-full border border-solid ${filterDraft.country === country.name.toLowerCase() ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
                onClick={() => setFilterDraft({ ...filterDraft, country: country.name.toLowerCase(), state: undefined })}
              >
                <div className="text-center font-medium">
                  {country.name} ({country.count})
                </div>
              </button>
            ))}
          </div>
        </div>

        {filterDraft.country && states.length > 0 && (
          <>
            <div className="h-px bg-gray-200 dark:bg-gray-700 my-6"></div>

            {/* Region/State filter */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">{t('region')}</h3>
              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  type="button"
                  className={`py-2 px-4 rounded-full border border-solid ${!filterDraft.state ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
                  onClick={() => setFilterDraft({ ...filterDraft, state: undefined })}
                >
                  <div className="text-center font-medium">{t('all')}</div>
                </button>
                {states.map((state) => (
                  <button
                    key={state.name}
                    type="button"
                    className={`py-2 px-4 rounded-full border border-solid ${filterDraft.state === state.name ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
                    onClick={() => setFilterDraft({ ...filterDraft, state: state.name })}
                  >
                    <div className="text-center font-medium">
                      {state.name} ({state.count})
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="h-px bg-gray-200 dark:bg-gray-700 my-6"></div>

        {/* Holes filter */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">{t('holes')}</h3>
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="button"
              className={`py-2 px-4 rounded-full border border-solid ${!filterDraft.minHoles && !filterDraft.maxHoles ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => setFilterDraft({ ...filterDraft, minHoles: undefined, maxHoles: undefined })}
            >
              <div className="text-center font-medium">{t('all')}</div>
            </button>
            <button
              type="button"
              className={`py-2 px-4 rounded-full border border-solid ${filterDraft.minHoles === 9 && filterDraft.maxHoles === 9 ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => setFilterDraft({ ...filterDraft, minHoles: 9, maxHoles: 9 })}
            >
              <div className="text-center font-medium">9 {t('holes')}</div>
            </button>
            <button
              type="button"
              className={`py-2 px-4 rounded-full border border-solid ${filterDraft.minHoles === 18 && filterDraft.maxHoles === 18 ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => setFilterDraft({ ...filterDraft, minHoles: 18, maxHoles: 18 })}
            >
              <div className="text-center font-medium">18 {t('holes')}</div>
            </button>
          </div>
        </div>

        <div className="h-px bg-gray-200 dark:bg-gray-700 my-6"></div>

        {/* Amenities filter */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">{t('amenities')}</h3>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <button
              type="button"
              className={`flex items-center justify-start p-3 rounded-full border border-solid ${isAmenitySelected('restaurant') ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => toggleAmenity('restaurant')}
            >
              <IonIcon icon={restaurant} className="mr-2" />
              <span>{t('restaurant')}</span>
            </button>
            <button
              type="button"
              className={`flex items-center justify-start p-3 rounded-full border border-solid ${isAmenitySelected('driving-range') ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => toggleAmenity('driving-range')}
            >
              <IonIcon icon={golfOutline} className="mr-2" />
              <span>{t('driving_range')}</span>
            </button>
            <button
              type="button"
              className={`flex items-center justify-start p-3 rounded-full border border-solid ${isAmenitySelected('rental-carts') ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => toggleAmenity('rental-carts')}
            >
              <IonIcon icon={cartOutline} className="mr-2" />
              <span>{t('rental_carts')}</span>
            </button>
            <button
              type="button"
              className={`flex items-center justify-start p-3 rounded-full border border-solid ${isAmenitySelected('pro-shop') ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => toggleAmenity('pro-shop')}
            >
              <IonIcon icon={bagRemoveOutline} className="mr-2" />
              <span>{t('pro_shop')}</span>
            </button>
            <button
              type="button"
              className={`flex items-center justify-start p-3 rounded-full border border-solid ${isAmenitySelected('putting-green') ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => toggleAmenity('putting-green')}
            >
              <IonIcon icon={basketOutline} className="mr-2" />
              <span>{t('putting_green')}</span>
            </button>
            <button
              type="button"
              className={`flex items-center justify-start p-3 rounded-full border border-solid ${isAmenitySelected('lockers') ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600'}`}
              onClick={() => toggleAmenity('lockers')}
            >
              <IonIcon icon={lockClosedOutline} className="mr-2" />
              <span>{t('lockers')}</span>
            </button>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-secondary shadow-lg">
          <IonButton expand="block" onClick={applyFilters}>
            {t('apply_filters')}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default GolfClubFilterModal;
